import { useState, useCallback, useId } from 'react';
import { Check, FlaskConical, AlertTriangle, Droplets } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { Beaker } from '@/components/lab';
import { copperCycleColors } from '@/config/experiments/chem1131/experiment5.config';

const stepInfoContent: Record<number, { title: string; content: string; whatHappens: string; observe: string[] }> = {
  1: {
    title: 'Adding Copper Metal',
    content: 'Copper (Cu) is a transition metal with characteristic reddish-orange color. In this step, we introduce approximately 0.5 g of copper wire that has been cleaned to remove surface oxidation. The clean, shiny surface ensures maximum reactivity.',
    whatHappens: 'The copper wire coil is placed in the beaker. At this point, no chemical reaction occurs - we are simply preparing the reactant for the next step.',
    observe: [
      'Reddish-orange metallic luster of cleaned copper',
      'Coiled wire shape for increased surface area',
      'No color change in the beaker yet'
    ]
  },
  2: {
    title: 'Adding Concentrated Nitric Acid',
    content: 'Concentrated HNO₃ (nitric acid) is a powerful oxidizing acid. Unlike dilute acids that simply donate protons, concentrated HNO₃ can oxidize metals through a redox reaction. The nitrogen in HNO₃ acts as the oxidizing agent.',
    whatHappens: 'When HNO₃ contacts copper, a vigorous reaction begins. Copper is oxidized (loses electrons) while nitrogen in HNO₃ is reduced (gains electrons).',
    observe: [
      'CAUTION: Brown toxic NO₂ gas evolves - work in fume hood!',
      'Solution begins to turn blue-green',
      'Copper wire visibly dissolves',
      'Bubbling indicates gas production'
    ]
  },
  3: {
    title: 'Observing the Redox Reaction',
    content: 'This is an oxidation-reduction (redox) reaction:\n\nCu + 4HNO₃(conc) -> Cu(NO3)2 + 2NO₂ + 2H₂O\n\nCopper is oxidized: Cu -> Cu²⁺ + 2e-\nNitrogen is reduced: N(+5) -> N(+4) in NO₂\n\nThe brown gas (NO₂) is toxic nitrogen dioxide.',
    whatHappens: 'The copper dissolves as it is converted to Cu²⁺ ions in solution. The solution turns blue-green due to hydrated copper(II) ions. Brown NO₂ gas escapes.',
    observe: [
      'Brown fumes of NO₂ gas rising',
      'Solution color changing from colorless to blue-green',
      'Copper wire gradually disappearing',
      'Exothermic reaction - beaker may feel warm'
    ]
  },
  4: {
    title: 'Diluting the Solution',
    content: 'Adding distilled water dilutes the Cu(NO3)2 solution to approximately 100 mL. This dilution is important for the next reaction step and helps cool the solution. The blue-green color intensifies as we observe more hydrated Cu²⁺ ions.',
    whatHappens: 'Water is added to dilute the concentrated solution. The color may appear more uniform as the solution is mixed. No new chemical reaction occurs.',
    observe: [
      'Volume increases to approximately 100 mL',
      'Blue-green color of Cu(NO3)2 solution',
      'Solution should be clear (no solid particles)',
      'Ready for precipitation step'
    ]
  }
};

// Custom Copper Wire component
function CopperWire({ color, cleaned = false }: { color: string; cleaned?: boolean }) {
  const id = useId();
  return (
    <svg viewBox="0 0 60 80" width={50} height={70}>
      <defs>
        <linearGradient id={`copper-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={color} stopOpacity="0.8" />
          <stop offset="50%" stopColor={color} />
          <stop offset="100%" stopColor={color} stopOpacity="0.8" />
        </linearGradient>
      </defs>
      {/* Coiled copper wire */}
      <ellipse cx="30" cy="25" rx="20" ry="8" fill="none" stroke={`url(#copper-${id})`} strokeWidth="3" />
      <ellipse cx="30" cy="35" rx="18" ry="7" fill="none" stroke={`url(#copper-${id})`} strokeWidth="3" />
      <ellipse cx="30" cy="45" rx="16" ry="6" fill="none" stroke={`url(#copper-${id})`} strokeWidth="3" />
      <ellipse cx="30" cy="53" rx="14" ry="5" fill="none" stroke={`url(#copper-${id})`} strokeWidth="3" />
      {cleaned && (
        <g>
          <circle cx="45" cy="20" r="2" fill="#FFFFFF" opacity="0.6">
            <animate attributeName="opacity" values="0.6;0.2;0.6" dur="1s" repeatCount="indefinite" />
          </circle>
          <circle cx="15" cy="40" r="1.5" fill="#FFFFFF" opacity="0.5">
            <animate attributeName="opacity" values="0.5;0.2;0.5" dur="1.2s" repeatCount="indefinite" />
          </circle>
        </g>
      )}
      <text x="30" y="72" fontSize="8" fill="#E0E0E0" textAnchor="middle">Cu Wire</text>
    </svg>
  );
}

// Gas bubbles animation component
function GasBubbles({ color, active }: { color: string; active: boolean }) {
  if (!active) return null;
  return (
    <g>
      {[...Array(8)].map((_, i) => (
        <circle
          key={i}
          cx={35 + (i % 4) * 15}
          cy={100}
          r={2 + (i % 3)}
          fill={color}
          opacity={0.6}
        >
          <animate
            attributeName="cy"
            values="100;30;100"
            dur={`${1.5 + (i % 3) * 0.3}s`}
            repeatCount="indefinite"
            begin={`${i * 0.2}s`}
          />
          <animate
            attributeName="opacity"
            values="0.6;0.1;0.6"
            dur={`${1.5 + (i % 3) * 0.3}s`}
            repeatCount="indefinite"
            begin={`${i * 0.2}s`}
          />
        </circle>
      ))}
    </g>
  );
}

export default function Part1Dissolution() {
  const [copperAdded, setCopperAdded] = useState(false);
  const [acidAdded, setAcidAdded] = useState(false);
  const [reacting, setReacting] = useState(false);
  const [reactionComplete, setReactionComplete] = useState(false);
  const [waterAdded, setWaterAdded] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  const completedSteps = [copperAdded, acidAdded, reactionComplete, waterAdded].filter(Boolean).length;

  const handleAddCopper = useCallback(() => {
    if (!copperAdded) {
      setCopperAdded(true);
      setCurrentStep(2);
    }
  }, [copperAdded]);

  const handleAddAcid = useCallback(() => {
    if (copperAdded && !acidAdded) {
      setAcidAdded(true);
      setReacting(true);
      setCurrentStep(3);
      // Simulate reaction time
      setTimeout(() => {
        setReacting(false);
        setReactionComplete(true);
        setCurrentStep(4);
      }, 3000);
    }
  }, [copperAdded, acidAdded]);

  const handleAddWater = useCallback(() => {
    if (reactionComplete && !waterAdded) {
      setWaterAdded(true);
    }
  }, [reactionComplete, waterAdded]);

  const getSolutionColor = () => {
    if (waterAdded) return copperCycleColors.cuNO32Solution;
    if (reactionComplete) return copperCycleColors.cuNO32Solution;
    if (reacting) return copperCycleColors.reaction1Mid;
    if (acidAdded) return copperCycleColors.hno3Solution;
    return '#FFFFFF';
  };

  const getFillLevel = () => {
    if (waterAdded) return 70;
    if (acidAdded) return 20;
    return 0;
  };

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part III: Cu + HNO₃ (Redox)</h3>
        </div>
        <div className="safety-warning">
          <AlertTriangle size={14} />
          <span>Work in FUME HOOD - Toxic NO₂ gas!</span>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Add Copper', hint: 'Place copper coil in beaker', done: copperAdded },
            { id: 2, title: 'Add HNO₃', hint: 'Carefully add concentrated acid', done: acidAdded },
            { id: 3, title: 'Observe Reaction', hint: 'Watch bubbles and color change', done: reactionComplete },
            { id: 4, title: 'Add Water', hint: 'Dilute to 100 mL', done: waterAdded }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <LabBench
        experimentName="Copper Dissolution in HNO₃"
        completedCount={completedSteps}
        totalSteps={4}
        equipment={['Beaker', 'Copper wire', 'Concentrated HNO₃']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="reaction-workspace">
          <div className="equipment-row">
            {/* Copper Wire */}
            <div
              className={`equipment-station ${currentStep === 1 && !copperAdded ? 'clickable' : ''}`}
              onClick={handleAddCopper}
              style={{ cursor: currentStep === 1 && !copperAdded ? 'pointer' : 'default' }}
            >
              <CopperWire
                color={copperCycleColors.copperMetalClean}
                cleaned={true}
              />
              <span className="equipment-label">Cleaned Copper (~0.5g)</span>
            </div>

            {/* Reaction Beaker */}
            <div className="reaction-beaker-container">
              <svg viewBox="0 0 140 180" width={160} height={200}>
                <defs>
                  <linearGradient id="beaker-glass" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(170,200,220,0.7)" />
                    <stop offset="50%" stopColor="rgba(200,230,245,0.15)" />
                    <stop offset="100%" stopColor="rgba(170,200,220,0.6)" />
                  </linearGradient>
                  <linearGradient id="solution-grad" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor={getSolutionColor()} stopOpacity="0.85" />
                    <stop offset="100%" stopColor={getSolutionColor()} stopOpacity="0.95" />
                  </linearGradient>
                </defs>

                {/* Beaker body */}
                <rect x="25" y="25" width="90" height="120" fill="url(#beaker-glass)" stroke="#7A9BB8" strokeWidth="2" rx="2" />

                {/* Solution */}
                {getFillLevel() > 0 && (
                  <rect
                    x="28"
                    y={145 - getFillLevel() * 1.1}
                    width="84"
                    height={getFillLevel() * 1.1}
                    fill="url(#solution-grad)"
                  >
                    <animate
                      attributeName="fill"
                      values={reacting ? `${copperCycleColors.hno3Solution};${copperCycleColors.reaction1Mid};${copperCycleColors.cuNO32Solution}` : undefined}
                      dur={reacting ? "3s" : undefined}
                      fill="freeze"
                    />
                  </rect>
                )}

                {/* Copper in beaker */}
                {copperAdded && !reactionComplete && (
                  <g>
                    <ellipse cx="70" cy="130" rx="15" ry="5" fill={copperCycleColors.copperMetalClean} opacity={reacting ? 0.5 : 1}>
                      {reacting && (
                        <animate attributeName="opacity" values="1;0.3;0" dur="3s" fill="freeze" />
                      )}
                    </ellipse>
                  </g>
                )}

                {/* Gas bubbles */}
                {reacting && <GasBubbles color={copperCycleColors.no2Gas} active={true} />}

                {/* NO₂ gas cloud */}
                {reacting && (
                  <g>
                    <ellipse cx="70" cy="15" rx="30" ry="12" fill={copperCycleColors.no2Gas} opacity="0.4">
                      <animate attributeName="opacity" values="0;0.4;0.6;0.4" dur="2s" repeatCount="indefinite" />
                      <animate attributeName="ry" values="12;18;12" dur="2s" repeatCount="indefinite" />
                    </ellipse>
                  </g>
                )}

                <text x="70" y="165" fontSize="10" fill="#E0E0E0" textAnchor="middle">400 mL Beaker</text>
              </svg>
            </div>

            {/* HNO₃ Bottle */}
            <div
              className={`equipment-station ${currentStep === 2 && !acidAdded ? 'clickable' : ''}`}
              onClick={handleAddAcid}
              style={{ cursor: currentStep === 2 && !acidAdded ? 'pointer' : 'default' }}
            >
              <svg viewBox="0 0 50 80" width={45} height={75}>
                <rect x="15" y="25" width="20" height="45" fill="#FFFACD" stroke="#888" strokeWidth="1" rx="2" />
                <rect x="18" y="15" width="14" height="12" fill="#444" rx="2" />
                <text x="25" y="50" fontSize="6" fill="#333" textAnchor="middle">HNO₃</text>
                <text x="25" y="58" fontSize="5" fill="#666" textAnchor="middle">conc.</text>
                <text x="25" y="78" fontSize="7" fill="#E0E0E0" textAnchor="middle">Acid</text>
              </svg>
              <span className="equipment-label">Concentrated HNO₃</span>
            </div>

            {/* Water Bottle */}
            <div
              className={`equipment-station ${currentStep === 4 && !waterAdded ? 'clickable' : ''}`}
              onClick={handleAddWater}
              style={{ cursor: currentStep === 4 && !waterAdded ? 'pointer' : 'default' }}
            >
              <svg viewBox="0 0 50 80" width={45} height={75}>
                <rect x="15" y="25" width="20" height="45" fill="#E6F3FF" stroke="#888" strokeWidth="1" rx="2" />
                <rect x="18" y="15" width="14" height="12" fill="#3B82F6" rx="2" />
                <text x="25" y="50" fontSize="6" fill="#333" textAnchor="middle">H₂O</text>
                <text x="25" y="78" fontSize="7" fill="#E0E0E0" textAnchor="middle">Water</text>
              </svg>
              <span className="equipment-label">Distilled Water</span>
            </div>
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {currentStep === 1 && !copperAdded && (
              <div className="status-message info">
                <Droplets size={20} />
                Place the copper wire coil into the beaker
              </div>
            )}
            {currentStep === 2 && !acidAdded && (
              <div className="status-message warning">
                <AlertTriangle size={20} />
                Carefully add 4-5 mL concentrated HNO₃ (in fume hood!)
              </div>
            )}
            {reacting && (
              <div className="status-message reaction">
                <FlaskConical size={20} />
                Reaction in progress - Brown NO₂ gas evolving...
              </div>
            )}
            {reactionComplete && !waterAdded && (
              <div className="status-message info">
                <Droplets size={20} />
                Add distilled water to dilute to ~100 mL
              </div>
            )}
            {waterAdded && (
              <div className="status-message success">
                <Check size={20} />
                Cu(NO3)2 solution ready! Note the blue-green color.
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Redox Reaction</h4>
            <div className="equation">
              Cu + 4HNO₃ → Cu(NO3)2 + 2NO₂ + 2H₂O
            </div>
            <p>
              Copper is oxidized (Cu → Cu²⁺ + 2e-) while nitrogen in HNO₃ is reduced (N+5 → N+4).
              The brown gas is toxic nitrogen dioxide (NO₂).
            </p>
          </div>
        </div>

        <style>{`
          .reaction-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .equipment-row {
            display: flex;
            gap: 30px;
            align-items: flex-end;
            justify-content: center;
          }

          .equipment-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .equipment-station.clickable {
            cursor: pointer;
          }

          .equipment-station.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .equipment-label {
            font-size: 11px;
            color: var(--text-secondary);
            text-align: center;
          }

          .reaction-beaker-container {
            position: relative;
          }

          .safety-warning {
            display: flex;
            align-items: center;
            gap: 8px;
            background: rgba(239, 68, 68, 0.2);
            color: #EF4444;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 11px;
            margin: 8px 0;
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.warning {
            background: rgba(245, 158, 11, 0.2);
            color: #FBBF24;
          }

          .status-message.reaction {
            background: rgba(139, 69, 19, 0.3);
            color: #D2691E;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box .equation {
            font-family: 'Courier New', monospace;
            background: rgba(0, 0, 0, 0.3);
            padding: 8px 12px;
            border-radius: 4px;
            margin-bottom: 8px;
            color: #40E0D0;
          }

          .theory-box p {
            margin: 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp5"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
